import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";

// Encapsulates all analyst-chat state + side effects so the Chat component
// stays a thin view. Fixes stale-closure issues via useCallback dependencies.
export function useChat(onIncludedChange) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getChat();
      setMessages(res.messages || []);
    } catch (error) {
      logError("Failed to load chat history:", error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = useCallback(
    async (text) => {
      const q = (text ?? "").trim();
      if (!q || loading) return;
      setLoading(true);
      const tempId = `temp-${Date.now()}`;
      setMessages((m) => [...m, { id: tempId, question: q, answer: null, included: false }]);
      try {
        const res = await api.chat(q);
        setMessages((m) => m.map((msg) => (msg.id === tempId ? res : msg)));
      } catch (e) {
        setMessages((m) => m.filter((msg) => msg.id !== tempId));
        toast.error(e.response?.data?.detail || "Analyst chat failed.");
      } finally {
        setLoading(false);
      }
    },
    [loading]
  );

  const toggleInclude = useCallback(
    async (msg) => {
      const next = !msg.included;
      setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, included: next } : x)));
      try {
        await api.setChatIncluded(msg.id, next);
        onIncludedChange?.();
        toast.success(next ? "Added to report." : "Removed from report.");
      } catch (error) {
        logError("Failed to update report inclusion:", error);
        setMessages((m) => m.map((x) => (x.id === msg.id ? { ...x, included: !next } : x)));
        toast.error("Could not update.");
      }
    },
    [onIncludedChange]
  );

  const remove = useCallback(
    async (msg) => {
      setMessages((m) => m.filter((x) => x.id !== msg.id));
      try {
        await api.deleteChat(msg.id);
        if (msg.included) onIncludedChange?.();
      } catch (error) {
        logError("Failed to delete chat message:", error);
        load();
      }
    },
    [onIncludedChange, load]
  );

  return { messages, loading, send, toggleInclude, remove };
}
